import { crc32, UInt64, PCG32, shuffle, mkNode, safeJsonParse, getFreeSpace, isIndexed, escapeHtml } from '@p4b/utils';
import { dbGet, dbCursor, dbDelete, StorageError, dbPutKeyPath, dbPut, dbUpdate } from '@p4b/utils-db';
import { IndexedDBReader, IDBReader, IDBWriter, ExamId } from '@p4b/utils-zip';
import { HttpError, /*postJson,*/ getText, /*, examsApi*/
rangeSupported, getJson, requestJson, streamArrayBuffer, getArrayBuffer,
enableRemoteLogging, isRemoteLogging, isNetworkBlocked, ingestDomain} from '@p4b/utils-net';
import { Frame, Img, IType } from '@p4b/image-base';
import { makeStd, StdRenderer } from '@p4b/Renderers/render-std';
import { makeTiff, tiffDetect, TiffRenderer } from '@p4b/Renderers/render-tiff';
import { makePdf, PdfRenderer } from '@p4b/Renderers/render-pdf';
import { makeDicom, DicomRenderer, DicomImg, addDicom, DicomFrame } from '@p4b/Renderers/render-dicom';
import { QuestionManifest, AnswerResources, AnswerValue, isAnswerValue } from '@p4b/question-base';
import { Progress } from '@p4b/utils-progress';
import { alertModal } from "@p4b/modal-dialog";
import { j2kDetect, J2kRenderer, makeJ2k } from '@p4b/Renderers/render-jpeg2k';
import { translate } from '@p4b/utils-lang';
import { isSchedule } from '@p4b/exam-timer';
import { ExamState, isState } from '@p4b/exam-viewer';
import { ImageData } from 'canvas';
import { Writer, ZipReader, TextWriter, Entry, FileEntry } from '@zip.js/zip.js';
import examValidate from '@gen/validate_exam';
//import { ExamAdmin, ExamItem, ExamAdminOrList } from '@p4b/p4b-api';

export class ExamError extends Error {
    constructor(message: string) {
        super(message);
        Object.defineProperty(this, 'name', { value: 'ExamError' });
        Object.setPrototypeOf(this, new.target.prototype);
        Error?.captureStackTrace(this, ExamError);
    }
}

export type ExamMap = Map<string, ExamItem>;

export function splitExamId(key: string): ExamId {
    const parts = key.split('-');
    if (parts.length === 2) {
        return [parts[0], parseInt(parts[1])];
    }
    throw TypeError('invalid exam id');
}

export interface RemoteExamItem {
    id: string;
    component: number;
    version: number;
    title: string;
    full_title: string;
    link: string;
    proctored?: boolean;
    demo?: boolean;
    pin?: string;
    state?: string;
    autostart?: boolean;
}

export interface ExamItem extends RemoteExamItem {
    size: number;
    ranged: boolean;
}

export function isRemoteExamItem(x: unknown): x is RemoteExamItem {
    return isIndexed(x)
        && typeof x.id === 'string'
        && typeof x.component === 'number'
        && typeof x.version === 'number'
        && typeof x.title === 'string'
        && typeof x.full_title === 'string'
        && typeof x.link === 'string'
        && (typeof x.proctored === 'boolean' || typeof x.proctored === 'undefined')
        && (typeof x.demo === 'boolean' || typeof x.demo === 'undefined')
        && (typeof x.pin === 'string' || typeof x.pin === 'undefined')
        && (typeof x.state === 'string' || typeof x.state === 'undefined')
        && (typeof x.autostart === 'boolean' || typeof x.autostart === 'undefined');
}
export function isExamItem(x: unknown): x is ExamItem {
    return isIndexed(x) && isRemoteExamItem(x)
        && typeof x.size === 'number'
        && typeof x.ranged === 'boolean';
}

export interface FetchExamItem extends RemoteExamItem {
    size?: number;
    ranged?: boolean;
}

export interface Structure {
    backendQid: number[];
    backendAid: number[];
    answerType: string[];
    indents?: number[];
    displayQstNumber: string;
    displayAnsNumber: (string|undefined)[];
    length: number;
    visible: (Expr | undefined)[];
    question: number;
    factor?: string;
    interview?: number;
    round?: number;
    circuit?: number;
    room?: number;
    case?: number;
    ofCases?: number;
    mandatory?: boolean[];
}

export interface State {
    candidateId: string;
    candidatePin?: string;
    badPin?: string;
    examPin?: string;
    autoPin?: boolean;
    chosenExamId?: string;
    //exams: ExamMap;
    //structure: Structure[]; // (HTMLElement[] | Structure)[];
    //admin?: boolean;
    //qid?: number;
    //aid?: number;
    //downloaded?: boolean;
    //autologin?: boolean;
}

const stats = {
    answerReadMinTime: Number.POSITIVE_INFINITY,
    answerReadMaxTime: 0,
    answerReadTotalTime: 0,
    answerReadCount: 0,
    get answerReadAvgTime() {
        return (this.answerReadCount === 0) ? 0 : (this.answerReadTotalTime / this.answerReadCount);
    },
    answerWriteMinTime: Number.POSITIVE_INFINITY,
    answerWriteMaxTime: 0,
    answerWriteTotalTime: 0,
    answerWriteCount: 0,
    get answerWriteAvgTime() {
        return (this.answerWriteCount === 0) ? 0 : (this.answerWriteTotalTime / this.answerWriteCount);
    },
    statusUpdateMinTime: Number.POSITIVE_INFINITY,
    statusUpdateMaxTime: 0,
    statusUpdateTotalTime: 0,
    statusUpdateCount: 0,
    get statusUpdateAvgTime() {
        return (this.statusUpdateCount === 0) ? 0 : (this.statusUpdateTotalTime / this.statusUpdateCount);
    },
    unsubmittedReadMinTime: Number.POSITIVE_INFINITY,
    unsubmittedReadMaxTime: 0,
    unsubmittedReadTotalTime: 0,
    unsubmittedReadCount: 0,
    get unsubmittedReadAvgTime() {
        return (this.unsubmittedReadCount === 0) ? 0 : (this.unsubmittedReadTotalTime / this.unsubmittedReadCount);
    },
    unsubmittedUpdateMinTime: Number.POSITIVE_INFINITY,
    unsubmittedUpdateMaxTime: 0,
    unsubmittedUpdateTotalTime: 0,
    unsubmittedUpdateCount: 0,
    get unsubmittedUpdateAvgTime() {
        return (this.unsubmittedUpdateCount === 0) ? 0 : (this.unsubmittedUpdateTotalTime / this.unsubmittedUpdateCount);
    },
    frameReadMinTime: Number.POSITIVE_INFINITY,
    frameReadMaxTime: 0,
    frameReadTotalTime: 0,
    frameReadCount: 0,
    get frameReadAvgTime() {
        return (this.frameReadCount === 0) ? 0 : (this.frameReadTotalTime / this.frameReadCount);
    },
}

function logAnswerWriteTimeStats(time?: number): void {
    if (time !== undefined) {
        stats.answerWriteMinTime = Math.min(stats.answerWriteMinTime, time);
        stats.answerWriteMaxTime = Math.max(stats.answerWriteMaxTime, time);
        stats.answerWriteTotalTime += time;
        stats.answerWriteCount++;
    }
    console.info(`ANSWER_WRITE_TIME ${(time!==undefined)?`${time.toFixed(0)}ms `:''}min: ${stats.answerWriteMinTime.toFixed(0)}ms max: ${stats.answerWriteMaxTime.toFixed(0)}ms avg: ${stats.answerWriteAvgTime.toFixed(0)}ms`);
}

function logAnswerReadTimeStats(time?: number): void {
    if (time !== undefined) {
        stats.answerReadMinTime = Math.min(stats.answerReadMinTime, time);
        stats.answerReadMaxTime = Math.max(stats.answerReadMaxTime, time);
        stats.answerReadTotalTime += time;
        stats.answerReadCount++;
    }
    console.info(`ANSWER_READ_TIME ${(time!==undefined)?`${time.toFixed(0)}ms `:''}min: ${stats.answerReadMinTime.toFixed(0)}ms max: ${stats.answerReadMaxTime.toFixed(0)}ms avg: ${stats.answerReadAvgTime.toFixed(0)}ms`);
}

function logStatusUpdateTimeStats(time?: number): void {
    if (time !== undefined) {
        stats.statusUpdateMinTime = Math.min(stats.statusUpdateMinTime, time);
        stats.statusUpdateMaxTime = Math.max(stats.statusUpdateMaxTime, time);
        stats.statusUpdateTotalTime += time;
        stats.statusUpdateCount++;
    }
    console.info(`STATUS_UPDATE_TIME ${(time!==undefined)?`${time.toFixed(0)}ms `:''}min: ${stats.statusUpdateMinTime.toFixed(0)}ms max: ${stats.statusUpdateMaxTime.toFixed(0)}ms avg: ${stats.statusUpdateAvgTime.toFixed(0)}ms`);
}

function logUnsubmittedReadTimeStats(time?: number): void {
    if (time !== undefined) {
        stats.unsubmittedReadMinTime = Math.min(stats.unsubmittedReadMinTime, time);
        stats.unsubmittedReadMaxTime = Math.max(stats.unsubmittedReadMaxTime, time);
        stats.unsubmittedReadTotalTime += time;
        stats.unsubmittedReadCount++;
    }
    console.info(`UNSUBMITTED_READ_TIME ${(time!==undefined)?`${time.toFixed(0)}ms `:''}min: ${stats.unsubmittedReadMinTime.toFixed(0)}ms max: ${stats.unsubmittedReadMaxTime.toFixed(0)}ms avg: ${stats.unsubmittedReadAvgTime.toFixed(0)}ms`);
}

function logUnsubmittedUpdateTimeStats(time?: number): void {
    if (time !== undefined) {
        stats.unsubmittedUpdateMinTime = Math.min(stats.unsubmittedUpdateMinTime, time);
        stats.unsubmittedUpdateMaxTime = Math.max(stats.unsubmittedUpdateMaxTime, time);
        stats.unsubmittedUpdateTotalTime += time;
        stats.unsubmittedUpdateCount++;
    }
    console.info(`UNSUBMITTED_UPDATE_TIME ${(time!==undefined)?`${time.toFixed(0)}ms `:''}min: ${stats.unsubmittedUpdateMinTime.toFixed(0)}ms max: ${stats.unsubmittedUpdateMaxTime.toFixed(0)}ms avg: ${stats.unsubmittedUpdateAvgTime.toFixed(0)}ms`);
}

function logFrameReadTimeStats(time?: number): void {
    if (time !== undefined) {
        stats.frameReadMinTime = Math.min(stats.frameReadMinTime, time);
        stats.frameReadMaxTime = Math.max(stats.frameReadMaxTime, time);
        stats.frameReadTotalTime += time;
        stats.frameReadCount++;
    }
    console.info(`FRAME_READ_TIME ${(time!==undefined)?`${time.toFixed(0)}ms `:''}min: ${stats.frameReadMinTime.toFixed(0)}ms max: ${stats.frameReadMaxTime.toFixed(0)}ms avg: ${stats.frameReadAvgTime.toFixed(0)}ms`);
}

//----------------------------------------------------------------------------
// Assigment Data Model

export interface ExamAdmin {
    admin?: boolean;
    exams: RemoteExamItem[];
}

function isExamAdmin(x: unknown): x is ExamAdmin {
    return isIndexed(x) && Array.isArray(x.exams) && x.exams.every(isRemoteExamItem) &&
        (typeof x.admin === 'boolean' || typeof x.admin === 'undefined');
}

function isExamIndex(x: unknown): x is RemoteExamItem[] {
    return Array.isArray(x) && x.every(isRemoteExamItem);
}

export async function getExamList(): Promise<{admin: boolean; exams?: RemoteExamItem[]}> {
    const response = await getJson(ingestDomain, '/app/available_exams.json');
    let admin = false;
    let exams: RemoteExamItem[] | undefined = undefined;
    if (isExamAdmin(response)) {
        admin = response.admin === true;
        exams = response.exams;
    } else if (isExamIndex(response)) {
        exams = response;
    } else {
        throw new Error('Invalid exam list response');
    }
    if (exams) {
        for (const exam of exams) {
            if (isRemoteLogging(exam)) {
                await enableRemoteLogging(exam.send_logs);
                break;
            }
        }
    }
    return {admin, exams};
}

export interface ProctorExamity {
    redirectUrl: string;
    UserName?: string;
}

export interface Proctors {
    examity: ProctorExamity;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function isExamity(x: unknown): x is Proctors {
    return isIndexed(x) &&
        isIndexed(x.examity) && typeof x.examity.redirectUrl === 'string' &&
        (typeof x.examity.UserName === 'string' || typeof x.examity.UserName === 'undefined');
}

/**
 * Generic Proctor Data Model
 */
export interface GenericProctor {
    /**
     * The name of the proctor.
     */
    proctorName: string;
    /**
     * If <code>redirectUrl</code> is set, the browser will be redirected to this URL.
     * <p>
     * <b>Note</b>: <code>redirectUrl</code> and {@link redirectScript} are mutually exclusive.
     */
    redirectUrl?: string;
    /**
     * If <code>redirectScript</code> is set, the browser will execute this script.
     * <p>
     * <b>Note</b>: {@link redirectUrl} and <code>redirectScript</code> are mutually exclusive.
     */
    redirectScript?: string;
}

export function isGenericProctor(x: unknown): x is GenericProctor {
    return isIndexed(x) && typeof x.proctorName === 'string'
        && (typeof x.redirectUrl === 'string' || typeof x.redirectScript === 'string');
}

export async function getProctors(): Promise<unknown> {
    try {
        return await getJson(ingestDomain, '/app/proctors/');
    } catch (err) {
        if (err instanceof Error) {
            console.error(err.message);
        }
        return {};
    }
}

export function proctorExamity(config: ProctorExamity): void {
    console.debug('EXAMITY', config);
    const {redirectUrl, UserName} = config;
    if (UserName) {
        const form = mkNode('form', {attrib: {method: 'post', action: redirectUrl}});
        mkNode('input', {parent: form, attrib: {type: 'hidden', name: 'UserName', value: UserName}});
        document.body.appendChild(form);
        form.submit();
    } else {
        console.warn('LOAD EXAMITY REDIRECT', redirectUrl);
        const p4b = window.top?.document.getElementById('p4b');
        if (p4b instanceof HTMLIFrameElement) {
            p4b.src = redirectUrl;
        } else {
            window.location.href = redirectUrl;
        }
    }
}

//----------------------------------------------------------------------------
// Encrypted Exam Data Model

function isExam(x: unknown): x is PractiqueNet.ExamJson {
    return examValidate(x);
}

function isImageSet(x: PractiqueNet.ExamJson.Definitions.Image): x is PractiqueNet.ExamJson.Definitions.ImageSet {
    return (x as PractiqueNet.ExamJson.Definitions.ImageSet).set != undefined;
}

function isImageSingle(x: PractiqueNet.ExamJson.Definitions.Image): x is PractiqueNet.ExamJson.Definitions.ImageSingle {
    return (x as PractiqueNet.ExamJson.Definitions.ImageSingle).image != undefined;
}

function isResource(x: PractiqueNet.ExamJson.Definitions.Image): x is PractiqueNet.ExamJson.Definitions.Resource {
    return (x as PractiqueNet.ExamJson.Definitions.Resource).type != undefined;
}

export async function deleteExam(exams: ExamMap, examId?: string, notifyServer = false): Promise<void> {
    if (examId == null) {
        throw 'invalid exam id';
    }
    const dbKey = splitExamId(examId);
    await dbDelete('exams', dbKey);
    await dbDelete('encrypted', IDBKeyRange.bound(
        [...dbKey, 0],
        [...dbKey, Number.MAX_SAFE_INTEGER]
    ));
    if (notifyServer) {
        try {
            const response = await getJson(ingestDomain, `/app/${exams.get(examId)?.id}/deleted/`);
            if (isRemoteLogging(response)) {
                await enableRemoteLogging(response.send_logs);
            }
        } catch (err) {
            console.error('DELETE_EXAM', String(err));
        }
    }
    exams.delete(examId);
}

export async function updateExamPins(localExams: ExamMap, remoteExams: RemoteExamItem[]): Promise<void> {
    for (const remoteExam of remoteExams) {
        const key = [remoteExam.id, remoteExam.component].join('-');
        const localExam = localExams.get(key);
        if (localExam && localExam.pin !== remoteExam.pin) {
            const updated = {...localExam, pin: remoteExam.pin};
            console.debug('UPDATE_EXAM_PIN', key, updated);
            await dbPut('exams', updated);
            localExams.set(key, updated);
        }
    }
}

export async function getLocalExams(): Promise<ExamMap> {
    const exams = new Map<string, ExamItem>();
    await dbCursor('exams', undefined, (exam) => {
        if (isExamItem(exam)) {
            exams.set([exam.id, exam.component].join('-'), exam);
        } else {
            console.error('CORRUPT_EXAM', exam);
        }
    });
    console.debug('LOCAL_EXAMS', Array.from(exams.values()));
    return exams;
}

export async function deleteUnavailable(localExams: ExamMap, remoteExams: RemoteExamItem[]): Promise<void> {
    const deleteSet = new Set(localExams.keys());
    for (const exam of remoteExams) {
        deleteSet.delete([exam.id, exam.component].join('-'));
    }
    for (const examId of Array.from(deleteSet.values())) {
        await deleteExam(localExams, examId);
    }
}

export function getFetchList(localExams: ExamMap, remoteExams: RemoteExamItem[]): FetchExamItem[] {
    console.debug('EXAM_LIST_LOCAL', Array.from(localExams.values()));
    console.debug('EXAM_LIST_REMOTE', remoteExams);
    const fetchList: FetchExamItem[] = [];
    for (const remote of remoteExams.values()) {
        const key: string = [remote.id, remote.component].join('-');
        const local = localExams.get(key);
        if (!local || local.version < remote.version) {
            fetchList.push(remote);
        }
    }
    console.debug('FETCH_LIST', fetchList);
    return fetchList;
}

async function getExamSizes(fetchList: FetchExamItem[]): Promise<{sizes: Map<string, {ranged: boolean, size: number}>, failCount: number, alerts: string}> {
    const sizes = new Map<string, {ranged: boolean, size: number}>();
    let failCount = 0;
    let alerts = '';
    for (const exam of fetchList) {
        if (exam.size !== undefined && exam.ranged !== undefined) {
            console.debug('LOCAL SIZE & RANGE');
            sizes.set(exam.link, {ranged: exam.ranged, size: exam.size});
        } else {
            try {
                sizes.set(exam.link, await rangeSupported(`${ingestDomain}${exam.link}`));
            } catch (err) {
                const safeExamTitle = escapeHtml(exam.title);
                if (err instanceof HttpError && err.status == 412) {
                    console.warn(`Download count exceeded: ${exam.title}\n`);
                    alerts += `<p>${translate('DOWNLOAD_COUNT_EXCEEDED', {examTitle: safeExamTitle})}</p>`;
                } else {
                    console.error(exam.title, String(err));
                    alerts += `<p>${safeExamTitle}<br/><b>${escapeHtml(String(err))}</b></p>`;
                }
                ++failCount;
            }
        }
    }
    return {sizes, failCount, alerts};
}

async function storeExamChunks(examId: ExamId, pos: number, chunks: ArrayBuffer): Promise<void> {
    const size = chunks.byteLength;
    const chunkSize = 0x100001c;
    const count = Math.ceil(size / chunkSize);
    let start = 0;
    for (let i = pos; i < pos + count; ++i) {
        const end = (size - start > chunkSize) ? (start + chunkSize) : size;
        console.debug('PUT ENCRYPTED', [...examId, i]);
        await dbPut('encrypted', chunks.slice(start, end), [...examId, i]);
        console.debug ('ENCRYPTED DONE');
        start = end;
    }
}

async function fetchExam(id: ExamId, url: string, {ranged, size}: {ranged: boolean, size: number}, progress: Progress): Promise<{downloaded: number, checked: number}> {
    console.debug('RANGED', ranged, size);

    if (ranged) {
        let downloaded = 0, checked = 0;
        const chunkSize = 0x100001c;
        const chunks = Math.ceil(size / chunkSize);
        const lastChunk = (size === chunks * chunkSize) ? chunkSize : (size - (chunks - 1) * chunkSize);

        for (let n = 0; n < chunks; ++n) {
            console.debug('CHECK ENCRYPTED', [...id, n]);
            const buf = await dbGet<ArrayBuffer>('encrypted', [...id, n]);
            console.debug('LOCAL', buf?.byteLength, 'REMOTE', (n === chunks - 1) ? lastChunk : chunkSize);
            const start = chunkSize * n;
            const end = (start + chunkSize > size) ? size : start + chunkSize;
            if (!buf || ((n === chunks - 1) ? (buf.byteLength !== lastChunk) : (buf.byteLength !== chunkSize))) {
                // missing or incomplete chunk
                const start = chunkSize * n;
                const end = (start + chunkSize > size) ? size : start + chunkSize;
                console.debug('RANGED', start, end);
                const buf2 = await streamArrayBuffer({url, progress, start, end: end - 1});
                await dbPut('encrypted', buf2, [...id, n]);
                downloaded += buf2.byteLength;
            } else {
                console.debug(`CHECKED ${n} [${start}-${end}]`);
                await progress.show(end);
                checked += buf.byteLength;
            }
        }
        return {downloaded, checked};
    } else {
        console.debug('UNRANGED');
        const data = await getArrayBuffer(url, progress);
        await storeExamChunks(id, 0, data);
        return {downloaded: data.byteLength, checked: 0};
    }
}

export async function fetchExams(localExamMap: ExamMap, fetchList: FetchExamItem[], progress: Progress): Promise<boolean> {
    const sizes = await getExamSizes(fetchList);
    let {failCount, alerts} = sizes;
    const {sizes: heads} = sizes;
    console.debug('HEADS', heads);
    const downloadCount = fetchList.length;
    const totalSize = Array.from(heads.values()).reduce((a, b) => a + b.size, 0);

    if (totalSize === 0) {
        if (alerts) {
            throw new ExamError(alerts);
        }
        return false;
    }

    {
        const {used, quota} = await getFreeSpace();
        progress.setTotalSize(totalSize);
        progress.setTitle(translate('DOWNLOAD_TITLE', {downloadCount}));
        progress.setDescription(translate('DOWNLOAD_DESCRIPTION', {freeSpace: (used != null && quota != null) ? translate('FREE_SPACE', {used, quota}) : ''}));
        //await wait(3000);
        progress.onProgress = async () => {
            const {used, quota} = await getFreeSpace();
            progress.setDescription(translate('DOWNLOAD_DESCRIPTION', {freeSpace: (used != null && quota != null) ? translate('FREE_SPACE', {used, quota}) : ''}));
            //await wait(3000);
        }
    }

    console.debug('GOT SIZES', Array.from(heads.entries()));

    for (const exam of fetchList) {
        const head = heads.get(exam.link);
        if (head !== undefined) {
            const examId: ExamId = [exam.id, exam.component];
            try {
                const {downloaded, checked} = await fetchExam(examId, `${ingestDomain}${exam.link}`, head, progress);
                if (downloaded + checked === head.size) {
                    const updated = {...exam, size: head.size, ranged: head.ranged};
                    await dbPut('exams', updated);
                    localExamMap.set(examId.join('-'), updated);
                    if (downloaded > 0) {
                        const response = await getJson(ingestDomain, `/app/${exam.id}/downloaded/`);
                        if (isRemoteLogging(response)) {
                            await enableRemoteLogging(response.send_logs);
                        }
                    }
                } else if (downloaded + checked !== head.size) {
                    alerts += `<p>${escapeHtml(exam.title)}<br><b>Incomplete data transfer</b></p>`;
                }
            } catch(err) {
                if (err instanceof HttpError && err.status == 412) {
                    alerts += `<p>${translate('DOWNLOAD_COUNT_EXCEEDED', {examTitle: escapeHtml(exam.title)})}</p>`;
                } else if (err instanceof StorageError) {
                    throw err;
                } else {
                    alerts += `<p>${escapeHtml(exam.title)}<br/><b>${escapeHtml(String(err))}</b></p>`;
                }
                ++failCount;
            }
        }
    }
    if (alerts) {
        throw new ExamError(alerts);
    }
    return fetchList.length > failCount;
}

//----------------------------------------------------------------------------
// Prepare Exam

class ArrayBufferWriter extends Writer<ArrayBuffer> {
    private u8: Uint8Array<ArrayBuffer>;
    private p: number;
    private length: number;

    public constructor(length: number) {
        super();
        this.length = length;
        this.u8 = new Uint8Array(this.length);
        this.p = 0;
    }

    public async writeUint8Array(array: Uint8Array): Promise<void> {
        this.u8.set(array, this.p);
        this.p += array.length;
    }

    public async getData(): Promise<ArrayBuffer> {
        return this.u8.buffer;
    }
}

function countImages(images: PractiqueNet.ExamJson.Definitions.Image[]) {
    let x = 0;
    for (const img of images) {
        if (isImageSet(img)) {
            x += img.set.length;
        } else {
            ++x;
        }
    }
    return x;
}

function countQuestionsImages(used: Set<number>, questions: PractiqueNet.ExamJson.Definitions.Question[]) {
    let x = 0;
    for (const question of questions) {
        if (used.has(question.backend_id)) {
            //console.log('COUNT', question.backend_id);
            x += countImages(question.images);
            if (question.answersResources) {
                for (const answerResources of question.answersResources) {
                    x += countImages(answerResources);
                }
            }
            for (const answer of question.answers) {
                if (answer.type === 'SBA' && answer.optionDetails) {
                    for (const option of answer.optionDetails) {
                        x += countImages(option.resources ?? []);
                    }
                }
            }
        }
    }
    return x;
}

function countExamImages(used: Set<number>, exam: PractiqueNet.ExamJson): number {
    let x = countQuestionsImages(used, exam.questions);
    if (exam.variantQuestions) {
        for (const variant of exam.variantQuestions) {
            x += countQuestionsImages(used, variant);
        }
    }
    return x;
}

export interface Storage {
    unpackExam: (key: string, examId: ExamId, candidateId: string, size: number, title: string, progress: Progress) => Promise<{manifest: PractiqueNet.ExamJson, structure: Structure[]}>;
    getImageBegin: () => Promise<void>;
    getImageFrame: (start: number, end: number) => Promise<Uint8Array<ArrayBuffer>|undefined>;
    getImageEnd: () => Promise<void>;
}

function getStructure(manifest: PractiqueNet.ExamJson, candidateId: string): {structure: Structure[], used: Set<number>} {
    let sections: PractiqueNet.ExamJson.Definitions.Section[] | undefined = undefined;
    if (manifest.candidateSections) { // if sections provided lookup this CID.
        sections = manifest.candidateSections[candidateId];
    }
    if (sections == undefined && manifest.defaultSections) {
        sections = manifest.defaultSections;
    }
    const questionMap = new Map<number, number>();
    if (sections == undefined) {
        const order = [];
        for (let i = 0; i < manifest.questions.length; ++i) {
            const question = manifest.questions[i];
            order.push({ question: question.backend_id });
            questionMap.set(question.backend_id, i);
        }
        sections = [{
            items: order
        }];
    } else {
        for (let i = 0; i < manifest.questions.length; ++i) {
            questionMap.set(manifest.questions[i].backend_id, i);
        }
    }
    const examHash = crc32(manifest.exam.answer_aes_key);
    const candHash = crc32(candidateId);
    const random = new PCG32(new UInt64(candHash, examHash), new UInt64(examHash, candHash));
    for (const section of sections) {
        if (section.randomize || manifest.exam.randomize) {
            shuffle(random, section.items);
        }
    }
    const used = new Set<number>();
    let index = 0;
    let num = 1;
    const structure = [] as Structure[];
    for (const section of sections) {
        if (section.restartNumbering) {
            num = 1;
        }
        for (const item of section.items) {
            const cases = (item.cases != undefined) ? item.cases :
                (item.question != undefined) ? [item.question] :
                [];
            if (cases.length > 0) {
                for (const {question, caseIx} of cases.map((question, caseIx) => ({question, caseIx}))) {
                    used.add(question);
                    const i = questionMap.get(question);
                    if (i != undefined) {
                        const question = manifest.questions[i];
                        const variantIds = [question.backend_id];
                        if (manifest.variantQuestions) {
                            const x = manifest.variantQuestions[i];
                            for (let j = 0; j < x.length; ++j) {
                                variantIds.push(x[j].backend_id);
                                used.add(x[j].backend_id);
                            }
                        }
                        for (const answer of question.answers) {
                            if (answer.type === 'label') {
                                answer.mandatory = false; // make sure no label is marked mandatory
                            } else if (manifest.exam.disableBackwardsNavigation) {
                                answer.mandatory = true; // force answers to mandatory if backward navigation disabled
                            }
                        }
                        structure.push({
                            backendQid: variantIds,
                            backendAid: question.answers.map(a => a.backend_id),
                            answerType: question.answers.map(a => a.type),
                            indents: question.answers.map(a => a.indent ?? 0),
                            displayQstNumber: `${num++}`,
                            displayAnsNumber: question.answers.map(a => a.displayNumber),
                            length: question.answers.length,
                            visible: question.answers.map(a => a.visible),
                            question: index++,
                            factor: item.factor,
                            interview: item.interviewId,
                            round: item.round,
                            circuit: item.circuit,
                            room: item.room,
                            case: caseIx + 1,
                            ofCases: cases.length,
                            mandatory: question.answers.map(a => a.mandatory === true),
                        });
                    }
                }
            } else {
                if (!manifest.exam.hideEmptyItems) {
                    structure.push({
                        backendQid: [],
                        backendAid: [],
                        answerType: [],
                        indents: [],
                        displayQstNumber: `${num++}`,
                        displayAnsNumber: [],
                        length: 0,
                        visible: [],
                        question: index++,
                        factor: item.factor,
                        interview: item.interviewId,
                        round: item.round,
                        circuit: item.circuit,
                        room: item.room,
                        mandatory: [],
                    });
                }
            }
        }
    }
    return {structure, used};
}

export class StoreChunks implements Storage {
    private readChunkSize: number;
    private writeChunkSize: number;
    private imageReader: IDBReader | null;

    public constructor(readChunkSize: number, writeChunkSize: number) {
        this.readChunkSize = readChunkSize;
        this.writeChunkSize = writeChunkSize;
        this.imageReader = null;
    }

    public async unpackExam(key: string, examId: ExamId, candidateId: string, size: number, title: string, progress: Progress):
        Promise<{manifest: PractiqueNet.ExamJson, structure:Structure[]}>
    {
        console.debug('BEGIN UNZIP');
        const {used, quota} = await getFreeSpace();
        progress.setTitle(translate('PREPARING_TITLE'));
        progress.setDescription(translate('PREPARING_DESCRIPTION', {freeSpace: (used != null && quota != null) ? translate('FREE_SPACE', {used, quota}) : ''}));
        progress.onProgress = async () => {
            const {used, quota} = await getFreeSpace();
            progress.setDescription(translate('PREPARING_DESCRIPTION', {freeSpace: (used != null && quota != null) ? translate('FREE_SPACE', {used, quota}) : ''}));
        }

        const entries = await new ZipReader(new IndexedDBReader('encrypted', key, examId, this.readChunkSize, size)).getEntries();
        const manifest = await this.unpackEntries({candidateId, title, progress, entries});
        return manifest;
    }

    public async getImageBegin(): Promise<void> {
        this.imageReader = new IDBReader('images', this.writeChunkSize);
    }

    public async getImageFrame(start: number, end: number): Promise<Uint8Array<ArrayBuffer>|undefined> {
        if (end > start) {
            const t0 = performance.now();
            const data = await this.imageReader?.readUint8ArrayPromise(start, end);
            const t1 = performance.now();
            logFrameReadTimeStats(t1 - t0);
            return data;
        } else {
            return undefined;
        }
    }

    /*
    public getImageBlob(image: Img, frame: number): Promise<Blob> {
        if (image.dbOffset == null) {
            return Promise.reject('db_offset is null');
        }
        let start = image.dbOffset;
        for (let i = 0; i < frame; ++i) {
            start += image.dataSize[i];
        }
        const end = start + image.dataSize[frame];
        if (this.imageReader == null) {
            return Promise.reject('image_reader is null');
        }
        return this.imageReader.readBlob(start, end);
    }
    */

    public async getImageEnd(): Promise<void> {
        if (this.imageReader != null) {
            this.imageReader.close();
            this.imageReader = null;
        }
    }

    private async unpackEntries({entries, title, progress, candidateId}: {
        entries: Entry[],
        title: string,
        progress: Progress,
        candidateId: string
    }): Promise<{manifest: PractiqueNet.ExamJson, structure:Structure[]}> {
        const t0 = performance.now();
        console.debug('ENTRIES COUNT:', entries.length);
        console.debug('ENTRIES:');
        for (const entry of entries) {
            console.debug('+', entry.filename, 'size:', entry.uncompressedSize);
        }
        const files = entries.reduce((map, obj) => {
            if (!obj.directory) {
                map.set(obj.filename, obj);
            }
            return map;
        }, new Map<string, FileEntry>());
        const writer = new IDBWriter('images', this.writeChunkSize);
        const prepare = new PrepareExam(writer, files, progress, title);
        const manifest = await prepare.unpackManifest();

        if (manifest.candidates.indexOf(candidateId) < 0) {
            throw translate('ERROR_CANDIDATE_NOT_FOUND');
        }

        const {structure, used} = getStructure(manifest, candidateId);

        progress.setTotalSize(1 + countExamImages(used, manifest));
        await progress.show(1);

        await prepare.unpackQuestions(used, manifest.questions);
        manifest.questions.splice(0, manifest.questions.length);
        if (manifest.variantQuestions) {
            for (const variants of manifest.variantQuestions) {
                await prepare.unpackQuestions(used, variants);
                variants.splice(0, variants.length);
            }
            manifest.variantQuestions.splice(0, manifest.variantQuestions.length);
        }
        await writer.close();

        console.debug('TIMING', manifest.exam.timing);

        const t1 = performance.now();
        console.info('UNPACKING TIME: ', (t1 - t0), 'ms');
        return {manifest, structure};
    }
}


class PrepareExam {
    private writer: IDBWriter;
    private files: Map<string, FileEntry>;
    private progress: Progress;
    private count = 1;
    //private offset: Map<string, number> = new Map();
    private title: string

    constructor(writer: IDBWriter, files: Map<string, FileEntry>, progress: Progress, title: string) {
        this.writer = writer;
        this.files = files;
        this.progress = progress;
        this.title = title;
    }

    private async writeFrames(frames: Frame[]): Promise<void> {
        for (let i = 0; i < frames.length; ++i) {
            const data = frames[i].data;
            if (data != undefined) {
                frames[i].dbOffset = this.writer.getPtr();
                await this.writer.writeUint8Array(new Uint8Array(data));
                frames[i].data = undefined;

            }
        }
        await this.progress.show(++this.count);
    }

    private async writeFramesWithOverlays(frames: DicomFrame[]): Promise<void> {
        for (let i = 0; i < frames.length; ++i) {
            const data = frames[i].data;
            if (data != undefined) {
                frames[i].dbOffset = this.writer.getPtr();
                await this.writer.writeUint8Array(new Uint8Array(data));
                frames[i].data = undefined;
                for (let j = 0; j < frames[i].overlayRects.length; ++j) {
                    const bitmap = frames[i].overlayBitmaps[j];
                    if (bitmap != undefined) {
                        await this.writer.writeUint8Array(new Uint8Array(bitmap));
                        frames[i].overlayBitmaps[j] = undefined;
                    }
                }
            }
        }
        await this.progress.show(++this.count);
    }

    public async unpackDicomSet(imageSet: PractiqueNet.ExamJson.Definitions.ImageSet): Promise<{
        thumbnail?: ImageData, dicom?: Img
    }> {
        if (imageSet.set.length <= 0) {
            throw 'no images';
        }

        let thumbnail: ImageData | undefined;
        let dicom: DicomImg | undefined;
        for (let i = 0; i < imageSet.set.length; ++i) {
            const frame = imageSet.set[i];
            const entry = this.files.get(frame);
            if (entry) {
                try {
                    const data = await entry.getData(new ArrayBufferWriter(entry.uncompressedSize));
                    if (dicom == undefined) {
                        dicom = await makeDicom(data, frame);
                        dicom.caption = imageSet.caption || '';
                        dicom.distribution = imageSet.distribution;
                        dicom.use_dicom_order = imageSet.use_dicom_order ?? true;
                        const renderer = new DicomRenderer(dicom);
                        thumbnail = await renderer.renderThumbnail();
                        await this.writeFramesWithOverlays(dicom.frames);
                    } else {
                        await addDicom(dicom, data);
                        await this.writeFramesWithOverlays(dicom.frames);
                    }
                } catch (err) {
                    if (err instanceof Error) {
                        console.error('resource ' + frame + ', ' + err.message);
                        throw Object.create(err, {message: {value: 'resource ' + frame + ', ' + err.message}});
                    } else {
                        throw new Error('resource ' + frame + ', ' + String(err));
                    }
                }
            }
        }
        return {thumbnail, dicom};
    }

    public async unpackImageStd(image: PractiqueNet.ExamJson.Definitions.ImageSingle): Promise<{
        thumbnail?: ImageData, std?: Img
    }> {
        const entry = this.files.get(image.image);
        if (entry) {
            try {
                const data = await entry.getData(new ArrayBufferWriter(entry.uncompressedSize));
                const renderer = tiffDetect(data)
                    ? new TiffRenderer(makeTiff(image.image, [data]))
                    : (j2kDetect(data)
                        ? new J2kRenderer(makeJ2k(image.image, [data]))
                        : new StdRenderer(makeStd(image.image, [data]))
                    );
                const std = renderer.img;
                std.caption = image.caption || '';
                std.distribution = image.distribution;
                const thumbnail = await renderer.renderThumbnail();
                await this.writeFrames(std.frames);
                return {thumbnail, std};
            } catch (err) {
                if (err instanceof Error) {
                    console.error('resource ' + image.image + ', ' + err.message);
                    throw Object.create(err, {message: {value: 'resource ' + image.image + ', ' + err.message}});
                } else {
                    throw new Error('resource ' + image.image + ', ' + String(err));
                }
            }
        } else {
            const std: Img = {
                id: image.image,
                caption: image.caption || '',
                distribution: image.distribution,
                iType: IType.Unknown,
                frames: [],
                frameCount: 0,
            };
            return {std};
        }

    }

    public async unpackAudio(resource: PractiqueNet.ExamJson.Definitions.Resource): Promise<{
        thumbnail?: ImageData, audio?: Img
    }> {
        const entry = this.files.get(resource.file);
        if (entry) {
            const offset = this.writer.getPtr();
            await entry.getData(this.writer, {preventClose: true});
            const length = this.writer.getPtr() - offset;
            const audio = {
                id: resource.file,
                iType: IType.Audio,
                frames: [{
                    dbOffset: offset,
                    dataSize: length,
                }],
                caption: resource.caption || '',
                distribution: resource.distribution,
                mime: resource.mime ?? undefined,
                frameCount: 1,
            };
            await this.progress.show(++this.count);
            return {audio};
        } else {
            const audio: Img = {
                id: resource.file,
                iType: IType.Audio,
                caption: resource.caption || '',
                distribution: resource.distribution,
                mime: resource.mime ?? undefined,
                frames: [],
                frameCount: 0,
            };
            return {audio};
        }
    }

    public async unpackVideo(resource: PractiqueNet.ExamJson.Definitions.Resource): Promise<{
        thumbnail?: ImageData, video?: Img
    }> {
        const entry = this.files.get(resource.file);
        if (entry) {
            const offset = this.writer.getPtr();
            await entry.getData(this.writer, {preventClose: true});
            const length = this.writer.getPtr() - offset;
            const video: Img = {
                id: resource.file,
                iType: IType.Video,
                frames: [{
                    dbOffset: offset,
                    dataSize: length,
                }],
                caption: resource.caption || '',
                distribution: resource.distribution,
                mime: resource.mime ?? undefined,
                frameCount: 1,
            };
            await this.progress.show(++this.count);
            return {video};
        } else {
            const video: Img = {
                id: resource.file,
                iType: IType.Video,
                caption: resource.caption || '',
                distribution: resource.distribution,
                mime: resource.mime ?? undefined,
                frames: [],
                frameCount: 0,
            };
            return {video};
        }

    }

    public async unpackPdf(resource: PractiqueNet.ExamJson.Definitions.Resource): Promise<{
        thumbnail?: ImageData, pdf?: Img
    }> {
        const entry = this.files.get(resource.file);
        if (entry) {
            const data = await entry.getData(new ArrayBufferWriter(entry.uncompressedSize));
            const renderer = new PdfRenderer(makePdf(resource.file, [data]));
            const pdf = renderer.img;
            pdf.caption = resource.caption || '';
            pdf.distribution = resource.distribution;
            const thumbnail = await renderer.renderThumbnail();
            await this.writeFrames(pdf.frames);
            renderer.destroy();
            return {thumbnail, pdf};
        } else {
            const pdf = {
                id: resource.file,
                iType: IType.Pdf,
                caption: resource.caption || '',
                distribution: resource.distribution,
                frames: [],
                frameCount: 0,
            };
            return {pdf};
        }
    }

    public async unpackResources(images: PractiqueNet.ExamJson.Definitions.Image[]): Promise<{
        thumbnails: (ArrayBufferLike|null)[], resources: Img[]
    }> {
        const thumbnails: (ArrayBufferLike|null)[] = [];
        const resources: Img[] = [];
        for (let j = 0; j < images.length; ++j) {
            const image = images[j];
            if (isImageSet(image)) {
                const {dicom, thumbnail} = await this.unpackDicomSet(image);
                if (dicom) {
                    resources.push(dicom);
                    if (thumbnail?.data.buffer) {
                        thumbnails.push(thumbnail.data.buffer);
                    } else {
                        thumbnails.push(null);
                    }
                }
            } else if (isImageSingle(image)) {
                const {thumbnail, std} = await this.unpackImageStd(image);
                if (std) {
                    resources.push(std);
                    if (thumbnail?.data.buffer) {
                        thumbnails.push(thumbnail.data.buffer);
                    } else {
                        thumbnails.push(null);
                    }
                }
            } else if (isResource(image)) {
                switch (image.type) {
                    case 'video':
                        const resourceV = await this.unpackVideo(image);
                        if (resourceV.video) {
                            resources.push(resourceV.video);
                            thumbnails.push(null);
                        }
                        break;
                    case 'audio':
                        const resourceA = await this.unpackAudio(image);
                        if (resourceA.audio) {
                            resources.push(resourceA.audio);
                            thumbnails.push(null);
                        }
                        break;
                    case 'pdf':
                        const {thumbnail, pdf} = await this.unpackPdf(image);
                        if (pdf) {
                            resources.push(pdf);
                            if (thumbnail?.data.buffer) {
                                thumbnails.push(thumbnail.data.buffer);
                            } else {
                                thumbnails.push(null);
                            }
                        }
                        break;
                    default:
                        console.error('UNKNOWN RESOURCE', image);
                        thumbnails.push(null);
                        resources.push({
                            id: '',
                            iType: IType.Unknown,
                            frames: [],
                            frameCount: 0,
                        });
                        break;
                }
            } else {
                console.error('UNKNOWN FILE TYPE');
                thumbnails.push(null);
                resources.push({
                    id: '',
                    iType: IType.Unknown,
                    frames: [],
                    frameCount: 0,
                });
            }
        }
        return {thumbnails, resources};
    }

    public async unpackQuestions(used: Set<number>, questions: PractiqueNet.ExamJson.Definitions.Question[]): Promise<void> {
        for (const manifest of questions) {
            if (used.has(manifest.backend_id)) {
                console.debug('UNPACK', manifest.backend_id);
                try {
                    let thumbnails: (ArrayBufferLike|null)[] = []
                    let images: Img[] = [];
                    const answersResources: AnswerResources<ArrayBufferLike>[] = [];
                    if (manifest.images.length > 0) {
                        const unpacked = await this.unpackResources(manifest.images);
                        thumbnails = unpacked.thumbnails;
                        images = unpacked.resources;
                    }
                    if (manifest.answersResources) {
                        for (let i = 0; i < manifest.answersResources.length; ++i) {
                            const packed = manifest.answersResources[i];
                            const unpacked: AnswerResources<ArrayBufferLike> = await this.unpackResources(packed);
                            const options: AnswerResources<ArrayBufferLike>[] = [];
                            const answer = manifest.answers[i];
                            let optionResourcesCount = 0;
                            if (answer.type === 'SBA') {
                                //console.debug('SBA', answer.optionDetails);
                                for (const option of answer.optionDetails ?? []) {
                                    const packed = option.resources;
                                    if (packed) {
                                        console.debug('OPTION', packed);
                                        optionResourcesCount += packed.length;
                                        const unpacked = await this.unpackResources(packed);
                                        options.push(unpacked);
                                    } else {
                                        options.push({thumbnails: [], resources: []});
                                    }
                                }
                            }
                            if (optionResourcesCount > 0) {
                                unpacked.optionResources = options;
                            }
                            answersResources.push(unpacked);
                        }
                    }
                    for (const answer of manifest.answers) {
                        if (answer.type === 'hotspot' && answer.resource) {
                            const packed = [answer.resource];
                            const unpacked = await this.unpackResources(packed);
                            thumbnails = [...thumbnails, ...unpacked.thumbnails];
                            images = [...images, ...unpacked.resources];
                            break; // only get first image
                        }
                    }
                    const qm: QuestionManifest<ArrayBufferLike> = { manifest, images, thumbnails, answersResources };
                    await dbPut('questions', qm, manifest.backend_id);
                } catch (err) {
                    if (err instanceof Error) {
                        console.error('Question ' + manifest.backend_id + ', ' + err.message);
                        throw Object.create(err, {message: {value: 'Question ' + manifest.backend_id + ', ' + err.message}});
                    } else {
                        throw new Error('Question ' + manifest.backend_id + ', ' + String(err));
                    }
                }
            }
        }
    }

    public async unpackManifest(): Promise<PractiqueNet.ExamJson> {
        const manifestEntry = this.files.get('manifest.json');
        if (!manifestEntry) {
            throw new Error('Exam file has no manifest.');
        }
        const data = await manifestEntry.getData(new TextWriter('text/plain'));
        const maybeExam = safeJsonParse(data);
        if (!isExam(maybeExam)) {
            console.error('VALIDATION ERROR', examValidate.errors)
            console.error('EXAM DATA', maybeExam);
            throw new ValidationError(`<b>Invalid exam: ${this.title}</b><p>${examValidate.errors.map(x => escapeHtml(x.message)).join('<br>')}</p>`);
        }
        return maybeExam;
    }
}

export class ValidationError extends Error {
    constructor(message: string) {
        super(message);
        Object.defineProperty(this, 'name', { value: 'ValidationError' });
        Object.setPrototypeOf(this, new.target.prototype);
        Error?.captureStackTrace(this, ValidationError);
    }
}

/*
var storeBlobs = (function() {
    function unpackExam(state: State, key: string, exam_id: string, chunkSize: number, size: number) {
        utils.log('BEGIN UNZIP');
        var t0 = performance.now();

        var unpack_progress = {
            ui : utils.make_elements(progress_ui),
        } as ProgressUi;
        unpack_progress.ui.title.textContent = 'Preparing exam, please wait...';
        unpack_progress.ui.subtext.textContent = 'Preparing can take several minutes depending on your computer specification.';
        unpack_progress.ui.text.textContent = '0%';
        wi.progress.appendChild(unpack_progress.ui.progress_box);
        function progress(v: number) {
            unpack_progress.ui.text.textContent = (100 * v | 0) + '%';
        }

        state.structure = [];
        return zip_get_entries(new IndexedDBReader('encrypted', key, exam_id, chunkSize, size)).then(function(entries) {
                var files = entries.reduce(function(map, obj) {
                    map.set(obj.filename, obj);
                    return map;
                }, new Map());
                var manifest_entry = files.get('manifest.json');
                return zip_extract_text(manifest_entry).then(function(data) {
                    var exam = JSON.parse(data);
                    if (!exam) {
                        throw 'manifest not found.';
                    }
                    utils.log('got manifest.');
                    if (exam.candidates.indexOf(state.exam_cid) < 0) {
                        throw 'candidate not found';
                    }
                    var len = count_exam_images(exam);
                    var idx = 1;
                    progress(idx / len);
                    if (exam.exam && exam.exam.answer_aes_key) {
                        state.this_exam = exam.exam;
                    } else {
                        state.this_exam = {'answer_aes_key': exam.answer_aes_key};
                    }
                    var max_heap_size = 0;
                    return exam.questions.reduce(function(promise1: Promise<void>, question: QuestionManifest, i: number) {
                        return promise1.then(function() {
                            question.id = i;
                            utils.log('BEGIN IMAGES');
                            var t2 = performance.now();
                            return question.images.reduce(function(promise2, image, j) {
                                return promise2.then(function() {
                                    if (image.set) {
                                        var db_offset = j;
                                        var dicom: dicom_viewer.DicomData = null;
                                        var data_size: number[] = [];
                                        var sizes: [number, number][] = [];
                                        var blob = new Blob([]);
                                        return image.set.reduce(function(promise3: Promise<number>, frame: string, k: number) {
                                            return promise3.then(function(cnt) {
                                                var entry = files.get(frame);
                                                return zip_extract_arraybuffer(entry).then(function(data) {
                                                    var new_dicom = dicom_viewer.make_dicom(data);
                                                    data = null;
                                                    if (!dicom || dicom_viewer.compatible(dicom, new_dicom)) {
                                                        dicom = new_dicom;
                                                        dicom.data.forEach(function(frame, x) {
                                                            data_size.push(frame.byteLength);
                                                            sizes.push([dicom.cols, dicom.rows]);
                                                            blob = new Blob([blob, frame]);
                                                            dicom.data[x] = null;
                                                            ++cnt;
                                                        })
                                                    }
                                                    progress(++idx / len);
                                                    return cnt;
                                                });
                                            });
                                        }, Promise.resolve(0)).then(function(cnt: number) {
                                            return storeImage([i, j], blob).then(function() {
                                                blob = null;
                                                max_heap_size = Math.max(max_heap_size, dicom_viewer.heap_size(dicom));
                                                dicom.data = [];
                                                dicom.frames = cnt;
                                                dicom.db_offset = db_offset;
                                                dicom.dataSize = data_size;
                                                dicom.sizes = sizes;
                                                dicom.cols = sizes[0][0];
                                                dicom.rows = sizes[0][1];
                                                question.images[j] = dicom;
                                                dicom = null;

                                            });
                                        });
                                    }
                                });
                            }, Promise.resolve()).then(function() {
                                var t3 = performance.now();
                                utils.log('MAX HEAP SIZE:', max_heap_size.toString(16));
                                utils.log('IMAGES TIME:', (t3 - t2), 'ms');
                            });
                        }).then(function() {
                            return store_question(question);
                        }).then(function() {
                            exam.questions[i] = null;
                        });
                    }, Promise.resolve()).then(function() {
                        //reader.close();
                        wi.progress.removeChild(unpack_progress.ui.progress_box);
                        state.question_count = exam.questions.length;
                        var t1 = performance.now();
                        utils.log('UNPACKING TIME: ', (t1 - t0), 'ms');
                        state.webcrypto_aes_key = null;
                        state.asmcrypto_aes_key = null;
                        return state;
                    });
                });
        }).catch(function(err) {
            wi.progress.removeChild(unpack_progress.ui.progress_box);
            utils.log('error: unpackExam:', err);
            state.webcrypto_aes_key = null;
            state.asmcrypto_aes_key = null;
            return Promise.reject(err);
        });
    }

    function getThumbnails(state) {
        if (state.question.images.length === 0) {
            utils.log('getThumbnails: no images');
            return Promise.resolve(state);
        }
        utils.log('getThumbnails: start images');
        return state.question.images.reduce(function(promise, image, j) {
            return promise.then(function() {
                utils.log('getThumbnails: ', [state.question.id, j], image.dataSize[0]);
                return db_get('images', [state.question.id, j]);
            }).then(function(blob) {
                return readBlob(blob.slice(0, image.dataSize[0])).then(function(buf) {
                    image.data.push(buf);
                });
            });
        }, Promise.resolve()).then(function() {
            utils.log('getThumbnails: end images');
            return state;
        });
    }

    function getImage(image) {
        var t0 = performance.now();
        return db_get('images', [state.question.id, image.db_offset]).then(function(blob) {
            var size = image.dataSize;
            return utils.fold(new utils.RangeIterator(0, image.frames), Promise.resolve(0), function(promise, frame, i) {
                return promise.then(function(start) {
                    var end = start + size;
                    return readBlob(blob.slice(start, end)).then(function(buf) {
                        image.data[frame] = buf;
                        return end;
                    });
                });
            });
        }).then(function() {
            var t1 = performance.now();
            utils.log('I [', state.question.id, image.db_offset, '] ', t1 - t0, 'ms');
        });
    }

    var blob;
    function getImageBegin(image) {
        return db_get('images', [state.question.id, image.db_offset]).then(function(b) {
            blob = b;
        });
    }

    function getImageFrame(image, frame) {
        var start = 0;
        for (var i = 0; i < frame; ++i) {
            start += image.dataSize[i];
        }
        var end = start + image.dataSize[frame];
        return readBlob(blob.slice(start, end))
    }

    function getImageEnd() {
        blob = null;
    }

    return {
        unpackExam : unpackExam,
        getThumbnails : getThumbnails,
        getImage : getImage,
        getImageBegin : getImageBegin,
        getImageFrame : getImageFrame,
        getImageEnd : getImageEnd
    }
})();
*/

//----------------------------------------------------------------------------
// Response Data Model

export type Json = string | number | boolean | null | Json[] | { [key: string]: Json | undefined };

export interface ResponseStatus {
    statusRemote: number; // 0=local, 1=remote
    statusEmpty: number; // 0=answered, 1=empty
}

export interface LocalData extends AnswerValue {
    elapsed: number;
}

export interface LocalStatus extends LocalData {
    examId: string;
    candidateId: string;
    qno: number;
    ano: number;
    statusRemote: number;
    statusEmpty: number;
}

export interface RemoteData extends LocalData {
    exam: string;
    responder: string;
    question: number;
    response: number;
    factor?: string;
}

function isLocalData(x: unknown): x is LocalData {
    return isIndexed(x) && isAnswerValue(x) &&
        typeof x.elapsed === 'number'
}

function isLocalStatus(x: unknown): x is LocalStatus {
    return isIndexed(x) && isLocalData(x) &&
        typeof x.qno === 'number' &&
        typeof x.ano === 'number' &&
        typeof x.examId === 'string' &&
        typeof x.candidateId === 'string' &&
        typeof x.statusRemote === 'number' &&
        typeof x.statusEmpty === 'number';
}

interface Flag {
    qid: number;
    aid: number;
    flag: boolean;
}

function isFlag(x: unknown): x is Flag {
    return isIndexed(x) &&
        typeof x.qid === 'number' &&
        typeof x.aid === 'number' &&
        typeof x.flag === 'boolean';
}

type Expr = PractiqueNet.ExamJson.Definitions.Expr;

export type StatusResponse = {
    state?: ExamState;
    elapsed?: number;
    schedule?: PractiqueNet.ExamJson.Definitions.Schedule;
    responses?: unknown[];
    timestamp?: number;
    recv_timestamp?: number;
    returnResponses?: boolean;
    scheduleVersion?: number;
    checkId?: string;
    checkValue?: boolean|null;
}

export type StatusRequest = StatusResponse & {
    loggedIn: boolean;
}

export type RemoteStatus = Omit<StatusResponse, 'responses'>;

export function isStatusResponse(x: unknown): x is StatusResponse {
    if (!isIndexed(x)) {
        console.warn('StatusResponse is not an object');
        return false;
    }
    if (!(typeof x.state !== 'undefined' || (typeof x.state === 'string' && isState(x.state)))) {
        console.warn('StatusResponse has invalid state');
        return false;
    }
    if (!(typeof x.elapsed === 'undefined' || typeof x.elapsed === 'number')) {
        console.warn('StatusResponse has invalid elsapsed time');
        return false;
    }
    if (!(typeof x.responses === 'undefined' || Array.isArray(x.responses))) {
        console.warn('StatusResponse has invalid responses');
        return false;
    }
    if (!(typeof x.schedule === 'undefined' || isSchedule(x.schedule))) {
        console.warn('StatusResponse has invalid schedule');
        return false;
    }
    return true;
}

function isRemoteData(x: unknown): x is RemoteData {
    return isIndexed(x) && isLocalData(x) &&
        typeof x.exam === 'string' &&
        typeof x.responder === 'string' &&
        typeof x.question === 'number' &&
        typeof x.response === 'number' &&
        (typeof x.factor === 'undefined' || typeof x.factor === 'string');
}

export class ResponseModel {
    private examId: string;
    private candidateId: string;
    private items: Structure[];
    private demo: boolean;

    constructor(examId: string, candidateId: string, demo: boolean, items: Structure[]) {
        this.examId = examId;
        this.candidateId = candidateId;
        this.items = items;
        this.demo = demo;
    }

    async getLastQuestion(): Promise<LocalStatus|undefined> {
        let last: LocalStatus | undefined;
        await dbCursor('answers', IDBKeyRange.bound(
            [this.examId, this.candidateId, 0, -1],
            [this.examId, this.candidateId, Number.MAX_SAFE_INTEGER, -1]
        ), answer => {
            if (isLocalStatus(answer) && answer.ano === -1) {
                if (last === undefined || answer.elapsed > last.elapsed) {
                    last = answer;
                }
            }
        });
        return last;
    }

    async fetchStatus(localState: StatusRequest, silent = false): Promise<{remoteStatus?: StatusResponse, timestamp?: number}> {
        if (this.demo || isNetworkBlocked()) {
            return {};
        }
        try {
            const {data, req_timestamp, rsp_timestamp} = await requestJson(ingestDomain, `/app/${this.examId}/status/`, localState);
            if (isRemoteLogging(data)) {
                await enableRemoteLogging(data.send_logs);
            }
            if (req_timestamp && rsp_timestamp) {
                if (isStatusResponse(data)) {
                    const timestamp = (data.timestamp && data.recv_timestamp)
                        ? rsp_timestamp - ((rsp_timestamp - req_timestamp) - (data.timestamp - data.recv_timestamp)) / 2
                        : rsp_timestamp;
                    logStatusUpdateTimeStats(rsp_timestamp - req_timestamp);
                    return {remoteStatus: data, timestamp};
                }
            }
        } catch (err) {
            console.error('FETCH_STATUS', err);
            if (!silent) {
                if (err instanceof HttpError && err.status === 502) {
                    await alertModal(translate('ERROR_PROXY'));
                } else {
                    await alertModal(translate('ERROR_RESEND', {err: escapeHtml(String(err))}));
                }
            }
        }
        return {};
    }

    async fetchStatusSync(localState: StatusResponse, silent = false): Promise<{remoteStatus?: StatusResponse, timestamp?: number}> {
        if (this.demo) {
            return {};
        }
        try {
            const {data, req_timestamp, rsp_timestamp} = await requestJson(ingestDomain, `/app/${this.examId}/status/`, localState);
            if (isRemoteLogging(data)) {
                await enableRemoteLogging(data.send_logs);
            }
            if (req_timestamp && rsp_timestamp) {
                console.debug(`STATUS FETCH TIME ${(rsp_timestamp - req_timestamp).toFixed(9)}ms`);
                if (isStatusResponse(data)) {
                    const timestamp = (data.timestamp && data.recv_timestamp)
                        ? rsp_timestamp - ((rsp_timestamp - req_timestamp) - (data.timestamp - data.recv_timestamp)) / 2
                        : rsp_timestamp;
                    return {remoteStatus: data, timestamp};
                }
            }
        } catch (err) {
            console.error('FETCH_STATUS', err);
            if (!silent) {
                if (err instanceof HttpError && err.status === 502) {
                    await alertModal(translate('ERROR_PROXY'));
                } else {
                    await alertModal(translate('ERROR_RESEND', {err: escapeHtml(String(err))}));
                }
            }
            return {};
        }
        return {};
    }

    toLocal(remote: RemoteData): LocalStatus {
        const {exam, responder, question, response, factor, ...value} = remote;
        const qno = this.items.findIndex(x => x.backendQid[0] === question && (factor == undefined || x.factor === factor));
        return {
            examId: exam,
            candidateId: responder,
            qno,
            ano: response === 0 ? -1 : this.items[qno].backendAid.indexOf(response),
            statusRemote: 1,
            statusEmpty: (value.answer == null) ? 1 : 0,
            ...value
        }
    }

    async updateResponses({responses}: StatusResponse): Promise<void> {
        if (responses) {
            //const t0 = performance.now();
            const answers: LocalStatus[] = [];
            for (const item of responses) {
                if (isRemoteData(item) && item.exam === this.examId && item.responder === this.candidateId) {
                    answers.push(this.toLocal(item));
                }
            }
            await dbPutKeyPath('answers', answers);
            //const t1 = performance.now();
            //console.log(`Store fetched responses: ${t1 - t0}ms`);
        }
    }

    async getStatus(onStatus: (i: number, j: number, s: ResponseStatus) => void): Promise<void> {
        await dbCursor('answers', IDBKeyRange.bound(
            [this.examId, this.candidateId, 0, 0], // ignore time-on-question entries
            [this.examId, this.candidateId, Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER]
        ), value => {
            if (isLocalStatus(value)) {
                onStatus(value.qno, value.ano, {
                    statusRemote: value.statusRemote,
                    statusEmpty: value.statusEmpty
                });
            }
        });
    }

    async getFlags(): Promise<Flag[]> {
        const flags: Flag[] = [];
        await dbCursor('flags', IDBKeyRange.bound(
            [this.examId, this.candidateId, 0, 0], // ignore time-on-question entries
            [this.examId, this.candidateId, Number.MAX_SAFE_INTEGER, Number.MAX_SAFE_INTEGER]
        ), value => {
            if (isFlag(value)) {
                flags.push(value);
            }
        });
        return flags;
    }

    async setFlag(qno: number, ano: number, flag: boolean): Promise<void> {
        return dbPut('flags', {
            qid: qno,
            aid: ano,
            flag: flag
        }, [this.examId, this.candidateId, qno, ano]);
    }

    async getAnswer(itemIndex: number, fieldIndex: number): Promise<LocalData|undefined> {
        const t0 = performance.now();
        const key = [this.examId, this.candidateId, itemIndex, fieldIndex];
        const response = await dbGet('answers', key);
        if (isLocalData(response)) {
            const t1 = performance.now();
            logAnswerReadTimeStats(t1 - t0);
            return response;
        } else {
            return undefined;
        }
    }

    async setAnswerLocal(value: LocalStatus, onStatus: (i: number, j: number, s: ResponseStatus) => void): Promise<boolean> {
        if (!value) {
            console.error('SET_ANSWER_LOCAL: no value');
            return false;
        }
        const t0 = performance.now();
        try {
            await dbPut('answers', value);
            onStatus(value.qno, value.ano, {
                statusRemote: value.statusRemote,
                statusEmpty: value.statusEmpty
            });
            const t1 = performance.now();
            logAnswerWriteTimeStats(t1 - t0);
            return true;
        } catch (err) {
            console.error('SET_ANSWER_LOCAL', err);
            await alertModal(`Save answer failed: ${escapeHtml(String(err))}`);
            return false;
        }
    }

    toRemote(local: LocalStatus): RemoteData {
        const {examId, candidateId, qno, ano, statusRemote, statusEmpty, ...value} = local;
        return {
            exam: examId,
            responder: candidateId,
            question: this.items[qno]?.backendQid[0], // aways use primary vairant
            response: ano < 0 ? 0 : this.items[qno]?.backendAid[ano],
            factor: this.items[qno]?.factor,
            ...value
        }
    }

    async getUnsubmitted(): Promise<LocalStatus[]> {
        const t0 = performance.now();
        const unsubmitted : LocalStatus[] = [];
        await dbCursor('answers', IDBKeyRange.bound([0,0], [0,1]), value => { // [0 = local, X = empty]
            if (isLocalStatus(value)) {
                unsubmitted.push(value);
            }
        }, 'statusIndex');
        const t1 = performance.now();
        if (unsubmitted.length > 0) {
            logUnsubmittedReadTimeStats((t1 - t0) / unsubmitted.length);
        }
        return unsubmitted;
    }

    async setSubmitted(submitted: LocalStatus[], onStatus: (i: number, j: number, s: ResponseStatus) => void): Promise<void> {
        const t0 = performance.now();
        for (const sub of submitted) {
            let matched = false;
            await dbUpdate('answers', [sub.examId, sub.candidateId, sub.qno, sub.ano], local => {
                if (isLocalStatus(local)
                    && JSON.stringify(local.answer) === JSON.stringify(sub.answer)
                    && JSON.stringify(local.extra) === JSON.stringify(sub.extra)
                ) {
                    matched = true;
                    local.statusRemote = 1;
                    return {value: local};
                } else {
                    return;
                }
            });
            if (matched && sub.qno > -1 && sub.ano > -1) {
                onStatus(sub.qno, sub.ano, {
                    statusRemote: 1,
                    statusEmpty: (sub.answer == null) ? 1 : 0
                });
            }
        }
        const t1 = performance.now();
        if (submitted.length > 0) {
            logUnsubmittedUpdateTimeStats((t1 - t0) / submitted.length);
        }
    }
}
